// src/pages/Orders.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getAllOrders, updateOrderStatus } from '../services/api';

const BASE_URL = 'https://api.civicsetu.in';

const ORDER_STATUSES   = ['', 'PLACED', 'PROCESSING', 'COMPLETED', 'SHIPPED', 'DELIVERED', 'CANCELLED'];
const PAYMENT_STATUSES = ['', 'PENDING', 'SUCCESS', 'FAILED'];
const ORDER_TYPES      = ['', 'DIGITAL', 'PHYSICAL'];
const ENTITY_TYPES     = ['', 'VEHICLE', 'FAMILY'];
const MATERIAL_TYPES   = ['', 'PAPER', 'VINYL'];

function statusBadge(status) {
  const map = {
    PLACED:     'badge-warning',
    PROCESSING: 'badge-info',
    COMPLETED:  'badge-success',
    SHIPPED:    'badge-purple',
    DELIVERED:  'badge-success',
    CANCELLED:  'badge-danger',
  };
  return map[status] || 'badge-default';
}

function paymentBadge(status) {
  return status === 'SUCCESS' ? 'badge-success'
    : status === 'FAILED'    ? 'badge-danger'
    : 'badge-warning';
}

function fmt(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function isTerminal(status) {
  return ['COMPLETED', 'DELIVERED', 'CANCELLED'].includes(status);
}

// ── Print QR Code — credit card size on photo/vinyl paper ────────
function printQrCode(order, qrDataUrl) {
  if (!qrDataUrl) {
    alert('QR code not loaded yet. Please wait a moment and try again.');
    return;
  }

  const matNote = order.materialType === 'VINYL'
    ? '⚠️ Print on VINYL — Use label printer (Zebra / Brother QL)'
    : '🖨️ Print on PHOTO PAPER — Use inkjet printer for best quality';

  // Credit card size: 85.6mm × 54mm
  // At 96dpi: 323px × 204px — but we use mm directly for print accuracy
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>QR Code — ${order.orderNumber}</title>
<style>
  @page { size: 85.6mm 54mm; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    width: 85.6mm; height: 54mm;
    display: flex; align-items: center; justify-content: center;
    background: white;
    font-family: Arial, sans-serif;
  }
  .card {
    width: 85.6mm; height: 54mm;
    display: flex; align-items: center; justify-content: center;
  }
  .card img {
    width: 85.6mm; height: 54mm;
    object-fit: contain;
  }
  .note {
    position: fixed; bottom: 0; left: 0; right: 0;
    background: #fff3cd; color: #856404;
    font-size: 9px; text-align: center; padding: 2px;
    display: none;
  }
  @media print { .note { display: none; } }
</style></head><body>
  <div class="card">
    <img src="${qrDataUrl}" alt="QR Code"/>
  </div>
  <script>
    window.addEventListener('load', () => {
      setTimeout(() => window.print(), 400);
    });
  </scr` + `ipt>
</body></html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
}

// ── Print Address Label — A4 paper ────────────────────────────
function printAddressLabel(order) {
  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Address — ${order.orderNumber}</title>
<style>
  @page { size: A4; margin: 20mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, sans-serif; font-size: 13px; color: #222; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0066cc; padding-bottom: 12px; margin-bottom: 16px; }
  .logo { font-size: 18px; font-weight: 700; color: #0066cc; }
  .logo span { display: block; font-size: 11px; font-weight: 400; color: #666; margin-top: 2px; }
  .onum { font-size: 11px; color: #666; text-align: right; }
  .onum strong { display: block; font-size: 14px; color: #222; }
  .section { margin-bottom: 14px; border: 1px solid #dee2e6; border-radius: 6px; padding: 12px; }
  .stitle { font-weight: 700; font-size: 12px; margin-bottom: 10px; color: #0066cc; text-transform: uppercase; letter-spacing: .5px; }
  .row { display: flex; gap: 12px; margin-bottom: 6px; font-size: 13px; }
  .lbl { font-weight: 600; min-width: 110px; color: #555; }
  .addr-box { border: 2px dashed #0066cc; border-radius: 8px; padding: 16px; margin-top: 16px; }
  .addr-box .title { font-size: 11px; color: #0066cc; font-weight: 700; text-transform: uppercase; margin-bottom: 10px; letter-spacing: .5px; }
  .addr-name { font-size: 16px; font-weight: 700; margin-bottom: 4px; }
  .addr-phone { font-size: 14px; color: #0066cc; font-weight: 600; margin-bottom: 8px; }
  .addr-line { font-size: 13px; line-height: 1.6; }
  .tracking { margin-top: 12px; padding: 8px 12px; background: #f0f7ff; border-radius: 4px; font-size: 12px; }
  .footer { border-top: 1px solid #ddd; padding-top: 10px; margin-top: 16px; font-size: 10px; color: #aaa; text-align: center; }
  .from-box { background: #f8f9fa; border-radius: 6px; padding: 10px 14px; margin-bottom: 14px; font-size: 12px; color: #555; }
</style></head><body>
  <div class="header">
    <div class="logo">🛡️ CivicSetu<span>Shipping Label</span></div>
    <div class="onum">Order<br/><strong>${order.orderNumber}</strong><br/>${fmt(order.createdAt)}</div>
  </div>

  <div class="from-box">
    <strong>FROM:</strong> CivicSetu · civicsetu.in · info@civicsetu.in
  </div>

  <div class="addr-box">
    <div class="title">📦 Ship To</div>
    <div class="addr-name">${order.shippingName || '—'}</div>
    <div class="addr-phone">📞 ${order.shippingPhone || '—'}</div>
    <div class="addr-line">
      ${order.shippingAddressLine1 || ''}
      ${order.shippingAddressLine2 ? '<br/>' + order.shippingAddressLine2 : ''}
      <br/>${order.shippingCity || ''}, ${order.shippingState || ''}
      <br/>PIN: <strong>${order.shippingPincode || ''}</strong>
      ${order.shippingLandmark ? '<br/>Landmark: ' + order.shippingLandmark : ''}
    </div>
    ${order.trackingNumber ? `<div class="tracking">🚚 Tracking: <strong>${order.trackingNumber}</strong></div>` : ''}
  </div>

  <div class="section">
    <div class="stitle">📦 Package Contents</div>
    <div class="row"><span class="lbl">Item</span><span>CivicSetu QR Sticker — ${order.entityType}</span></div>
    <div class="row"><span class="lbl">Material</span><span>${order.materialType || 'PAPER'}</span></div>
    <div class="row"><span class="lbl">Quantity</span><span>${order.quantity || 1} piece(s)</span></div>
    <div class="row"><span class="lbl">Order Value</span><span>₹${order.totalAmount} (PAID)</span></div>
  </div>

  <div class="footer">
    CivicSetu Admin · ${new Date().toLocaleString('en-IN')} · civicsetu.in
  </div>
  <script>
    window.addEventListener('load', () => {
      setTimeout(() => window.print(), 400);
    });
  </scr` + `ipt>
</body></html>`;

  const win = window.open('', '_blank');
  win.document.write(html);
  win.document.close();
}

// ════════════════════════════════════════════════════════════════

// ── CSV Download ──────────────────────────────────────────────
function downloadCSV(orders) {
  const headers = [
    'Order Number','Date','Customer Name','Customer Phone','Customer Email',
    'Order Type','Entity Type','Quantity','Material',
    'Amount','Discount','Total Paid','Payment Status','Order Status',
    'Payment ID','Tracking Number','Shipped Date','Delivered Date',
    'Shipping Name','Shipping Phone','Shipping Address','City','State','Pincode'
  ];
  const rows = orders.map(o => [
    o.orderNumber,
    o.createdAt ? new Date(o.createdAt).toLocaleDateString('en-IN') : '',
    o.userName||'', o.userPhone||'', o.userEmail||'',
    o.orderType, o.entityType, o.quantity||1,
    o.materialType||(o.orderType==='DIGITAL'?'DIGITAL':'PAPER'),
    o.amount, o.discountAmount||0, o.totalAmount,
    o.paymentStatus, o.orderStatus, o.paymentId||'',
    o.trackingNumber||'',
    o.shippedAt   ? new Date(o.shippedAt).toLocaleDateString('en-IN')   : '',
    o.deliveredAt ? new Date(o.deliveredAt).toLocaleDateString('en-IN') : '',
    o.shippingName||'', o.shippingPhone||'',
    `${o.shippingAddressLine1||''} ${o.shippingAddressLine2||''}`.trim(),
    o.shippingCity||'', o.shippingState||'', o.shippingPincode||''
  ].map(v => `"${String(v).replace(/"/g,'""')}"`));
  const csv  = [headers.join(','), ...rows.map(r=>r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `civicsetu_orders_${new Date().toISOString().slice(0,10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

// ── Allowed status transitions ────────────────────────────────
function getAllowedStatuses(currentStatus, orderType) {
  if (orderType === 'DIGITAL') {
    const map = {
      PLACED:     ['PROCESSING', 'COMPLETED', 'CANCELLED'],
      PROCESSING: ['COMPLETED', 'CANCELLED'],
      COMPLETED:  [], CANCELLED: [],
    };
    return map[currentStatus] || [];
  } else {
    const map = {
      PLACED:     ['PROCESSING', 'CANCELLED'],
      PROCESSING: ['SHIPPED', 'CANCELLED'],
      SHIPPED:    ['DELIVERED', 'CANCELLED'],
      DELIVERED:  [], CANCELLED: [],
    };
    return map[currentStatus] || [];
  }
}

export default function Orders() {
  const [allOrders,      setAllOrders]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [filters,        setFilters]        = useState({ orderType: '', orderStatus: '', paymentStatus: '' });
  const [entityFilter,   setEntityFilter]   = useState('');
  const [materialFilter, setMaterialFilter] = useState('');
  const [searchText,     setSearchText]     = useState('');
  const [dateFrom,       setDateFrom]       = useState('');
  const [dateTo,         setDateTo]         = useState('');
  const [selected,       setSelected]       = useState(null);
  const [updating,       setUpdating]       = useState(false);
  const [newStatus,      setNewStatus]      = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [message,        setMessage]        = useState(null);
  const [detailOrder,    setDetailOrder]    = useState(null);
  const [qrLoading,      setQrLoading]      = useState(false);
  const [qrDataUrl,      setQrDataUrl]      = useState(null);

  const adminToken = localStorage.getItem('adminToken');

  const loadOrders = useCallback(() => {
    setLoading(true);
    getAllOrders(filters)
      .then(res => setAllOrders(res.data?.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Client-side filtering on top of server results
  const orders = useMemo(() => {
    let r = allOrders;
    if (entityFilter)   r = r.filter(o => o.entityType === entityFilter);
    if (materialFilter) r = r.filter(o => (o.materialType||'PAPER').toUpperCase() === materialFilter);
    if (searchText.trim()) {
      const s = searchText.trim().toLowerCase();
      r = r.filter(o =>
        (o.userName||'').toLowerCase().includes(s) ||
        (o.userPhone||'').toLowerCase().includes(s) ||
        (o.userEmail||'').toLowerCase().includes(s) ||
        (o.orderNumber||'').toLowerCase().includes(s)
      );
    }
    if (dateFrom) { const d = new Date(dateFrom); r = r.filter(o => o.createdAt && new Date(o.createdAt) >= d); }
    if (dateTo)   { const d = new Date(dateTo); d.setHours(23,59,59,999); r = r.filter(o => o.createdAt && new Date(o.createdAt) <= d); }
    return r;
  }, [allOrders, entityFilter, materialFilter, searchText, dateFrom, dateTo]);

  const clearFilters = () => {
    setFilters({ orderType: '', orderStatus: '', paymentStatus: '' });
    setEntityFilter(''); setMaterialFilter('');
    setSearchText(''); setDateFrom(''); setDateTo('');
  };
  const hasFilters = filters.orderType || filters.orderStatus || filters.paymentStatus ||
    entityFilter || materialFilter || searchText || dateFrom || dateTo;

  // ── Manage modal ──────────────────────────────────────────────
  const openModal = (order) => {
    setSelected(order);
    setNewStatus(order.orderStatus);
    setTrackingNumber(order.trackingNumber || '');
    setMessage(null);
  };

  const handleUpdate = async () => {
    if (!newStatus) return;

    // Payment must be completed before any status change
    if (selected.paymentStatus !== 'SUCCESS') {
      setMessage({ type: 'error', text: 'Cannot update order — payment has not been completed.' });
      return;
    }

    if (newStatus === 'SHIPPED' && (!trackingNumber || !trackingNumber.trim())) {
      setMessage({ type: 'error', text: 'Tracking number is required when marking order as SHIPPED.' });
      return;
    }

    if (newStatus === 'SHIPPED' && trackingNumber && trackingNumber.trim()) {
      const dup = allOrders.find(o =>
        o.id !== selected.id &&
        o.trackingNumber &&
        o.trackingNumber.trim().toLowerCase() === trackingNumber.trim().toLowerCase()
      );
      if (dup) {
        setMessage({ type: 'error', text: `Tracking number "${trackingNumber}" is already used for order ${dup.orderNumber}. Each shipment must have a unique tracking number.` });
        return;
      }
    }

    setUpdating(true);
    try {
      await updateOrderStatus(selected.id, newStatus, trackingNumber);
      setMessage({ type: 'success', text: 'Order status updated successfully' });
      loadOrders();
      setTimeout(() => setSelected(null), 1200);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Update failed' });
    } finally {
      setUpdating(false);
    }
  };

  // ── Order Details modal ───────────────────────────────────────
  const openDetailModal = async (order) => {
    setDetailOrder(order);
    setQrDataUrl(null);
    if (order.paymentStatus === 'SUCCESS') {
      setQrLoading(true);
      try {
        const res = await fetch(`${BASE_URL}/api/admin/orders/${order.id}/qr`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        if (res.ok) {
          const blob = await res.blob();
          setQrDataUrl(URL.createObjectURL(blob));
        }
      } catch (e) {
        console.error('QR fetch failed:', e);
      } finally {
        setQrLoading(false);
      }
    }
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Orders</h1>
          <p>Manage all QR code orders</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => downloadCSV(orders)} title="Download filtered orders as CSV">
            ⬇️ Download Report
          </button>
          <button className="btn btn-secondary" onClick={loadOrders}>🔄 Refresh</button>
        </div>
      </div>

      <div className="card">
        {/* Row 1 — Dropdown filters */}
        <div className="filter-bar" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <select value={filters.orderType} onChange={e => setFilters(f => ({ ...f, orderType: e.target.value }))}>
            <option value="">All Types</option>
            {ORDER_TYPES.filter(Boolean).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={entityFilter} onChange={e => setEntityFilter(e.target.value)}>
            <option value="">All Entities</option>
            {ENTITY_TYPES.filter(Boolean).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={materialFilter} onChange={e => setMaterialFilter(e.target.value)}>
            <option value="">All Materials</option>
            {MATERIAL_TYPES.filter(Boolean).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filters.orderStatus} onChange={e => setFilters(f => ({ ...f, orderStatus: e.target.value }))}>
            <option value="">All Statuses</option>
            {ORDER_STATUSES.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.paymentStatus} onChange={e => setFilters(f => ({ ...f, paymentStatus: e.target.value }))}>
            <option value="">All Payments</option>
            {PAYMENT_STATUSES.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {/* Row 2 — Search + Date range */}
        <div className="filter-bar" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <input
            type="text"
            className="form-control"
            placeholder="🔍 Search name, phone, email, order number..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ minWidth: 240, maxWidth: 320 }}
          />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>From</label>
            <input type="date" className="form-control" value={dateFrom}
              onChange={e => setDateFrom(e.target.value)} style={{ width: 140 }} />
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>To</label>
            <input type="date" className="form-control" value={dateTo}
              onChange={e => setDateTo(e.target.value)} style={{ width: 140 }} />
          </div>
          {hasFilters && (
            <button className="btn btn-secondary btn-sm" onClick={clearFilters}>✕ Clear All</button>
          )}
          <span style={{ color: '#666', fontSize: 13 }}>
            {orders.length} order{orders.length !== 1 ? 's' : ''}
            {hasFilters && allOrders.length !== orders.length && ` (of ${allOrders.length} total)`}
          </span>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner"/>Loading orders...</div>
        ) : orders.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">📦</div><p>No orders found</p></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Order Number</th>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Entity</th>
                  <th>Qty</th>
                  <th>Material</th>
                  <th>Amount</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, idx) => (
                  <tr key={order.id}>
                    <td style={{ color: '#888' }}>{idx + 1}</td>
                    <td>
                      <button
                        onClick={() => openDetailModal(order)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#0066cc', fontSize: 11, textDecoration: 'underline',
                          fontFamily: 'monospace', padding: 0
                        }}
                        title="Click to view full order details"
                      >
                        {order.orderNumber}
                      </button>
                    </td>
                    <td>
                      {order.userName}<br/>
                      <span style={{ fontSize: 11, color: '#888' }}>{order.userPhone}</span>
                    </td>
                    <td>
                      <span className={`badge ${order.orderType === 'PHYSICAL' ? 'badge-purple' : 'badge-info'}`}>
                        {order.orderType}
                      </span>
                    </td>
                    <td><span className="badge badge-default">{order.entityType}</span></td>
                    <td style={{ textAlign: 'center' }}>{order.quantity || 1}</td>
                    <td style={{ fontSize: 12, color: '#666' }}>
                      {order.materialType || (order.orderType === 'DIGITAL' ? '—' : 'PAPER')}
                    </td>
                    <td>₹{order.totalAmount}</td>
                    <td>
                      <span className={`badge ${paymentBadge(order.paymentStatus)}`}>
                        {order.paymentStatus}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${statusBadge(order.orderStatus)}`}>
                        {order.orderStatus}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#666' }}>
                      {new Date(order.createdAt).toLocaleDateString('en-IN')}
                    </td>
                    <td>
                      {isTerminal(order.orderStatus) ? (
                        <span style={{ fontSize: 12, color: '#28a745', fontWeight: 600 }}>
                          {order.orderStatus === 'CANCELLED' ? '❌ Cancelled' : '✅ Done'}
                        </span>
                      ) : order.paymentStatus !== 'SUCCESS' ? (
                        <button
                          className="btn btn-sm"
                          style={{ fontSize: 11, color: '#856404', background: '#fff3cd',
                            border: '1px solid #ffc107', borderRadius: 4, cursor: 'pointer', padding: '3px 8px' }}
                          onClick={() => alert(
                            `⚠️ Payment Pending\n\nOrder: ${order.orderNumber}\n\nPayment status is ${order.paymentStatus}.\n\nPlease complete the payment before changing the order status.\n\nIf payment was made, check Razorpay dashboard and verify the webhook was received.`
                          )}
                        >
                          ⏳ Awaiting Payment
                        </button>
                      ) : (
                        <button className="btn btn-secondary btn-sm" onClick={() => openModal(order)}>
                          Manage
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Order Details Modal ─────────────────────────────────── */}
      {detailOrder && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDetailOrder(null)}>
          <div className="modal" style={{ maxWidth: 660, width: '95vw' }}>
            <div className="modal-header">
              <h3>📋 Order Details</h3>
              <button className="modal-close" onClick={() => setDetailOrder(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>

              {/* Order Info */}
              <div style={{ background: '#f0f7ff', borderRadius: 8, padding: 14, marginBottom: 14, border: '1px solid #cce0ff' }}>
                <div style={{ fontWeight: 700, color: '#0066cc', marginBottom: 10, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  📦 Order Information
                </div>
                <div className="detail-grid">
                  {[
                    ['Order Number',  <code style={{ fontSize: 11 }}>{detailOrder.orderNumber}</code>],
                    ['Order Date',    fmt(detailOrder.createdAt)],
                    ['Order Type',    <span className={`badge ${detailOrder.orderType === 'PHYSICAL' ? 'badge-purple' : 'badge-info'}`}>{detailOrder.orderType}</span>],
                    ['Entity',        detailOrder.entityType],
                    ['Quantity',      detailOrder.quantity || 1],
                    ['Material',      detailOrder.materialType || (detailOrder.orderType === 'DIGITAL' ? 'Digital' : 'PAPER')],
                    ['Base Amount',   `₹${detailOrder.amount}`],
                    ['Discount',      detailOrder.discountAmount ? `— ₹${detailOrder.discountAmount}` : '₹0'],
                    ['Total Paid',    <strong>₹{detailOrder.totalAmount}</strong>],
                    ['Payment',       <span className={`badge ${paymentBadge(detailOrder.paymentStatus)}`}>{detailOrder.paymentStatus}</span>],
                    ['Order Status',  <span className={`badge ${statusBadge(detailOrder.orderStatus)}`}>{detailOrder.orderStatus}</span>],
                    ['Payment ID',    <span style={{ fontSize: 11 }}>{detailOrder.paymentId || '—'}</span>],
                  ].map(([label, val]) => (
                    <div key={label} className="detail-item">
                      <label>{label}</label><span>{val}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Customer Info */}
              <div style={{ background: '#f0fff4', borderRadius: 8, padding: 14, marginBottom: 14, border: '1px solid #b7e4c7' }}>
                <div style={{ fontWeight: 700, color: '#1a7340', marginBottom: 10, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  👤 Customer Details
                </div>
                <div className="detail-grid">
                  {[
                    ['Name',  detailOrder.userName  || '—'],
                    ['Phone', detailOrder.userPhone  || '—'],
                    ['Email', detailOrder.userEmail  || '—'],
                  ].map(([label, val]) => (
                    <div key={label} className="detail-item">
                      <label>{label}</label><span>{val}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping Address — physical only */}
              {detailOrder.orderType === 'PHYSICAL' && (
                <div style={{ background: '#fff8f0', borderRadius: 8, padding: 14, marginBottom: 14, border: '1px solid #ffd6a5' }}>
                  <div style={{ fontWeight: 700, color: '#c45c00', marginBottom: 10, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    📍 Shipping Address
                  </div>
                  <div className="detail-grid">
                    {[
                      ['Name',      detailOrder.shippingName      || '—'],
                      ['Phone',     detailOrder.shippingPhone      || '—'],
                      ['Address 1', detailOrder.shippingAddressLine1 || '—'],
                      ['Address 2', detailOrder.shippingAddressLine2 || '—'],
                      ['City',      detailOrder.shippingCity       || '—'],
                      ['State',     detailOrder.shippingState      || '—'],
                      ['Pincode',   detailOrder.shippingPincode    || '—'],
                      ['Landmark',  detailOrder.shippingLandmark   || '—'],
                    ].map(([label, val]) => (
                      <div key={label} className="detail-item">
                        <label>{label}</label><span>{val}</span>
                      </div>
                    ))}
                  </div>
                  {detailOrder.trackingNumber && (
                    <div style={{ marginTop: 10, padding: '8px 12px', background: '#fff', borderRadius: 6, border: '1px solid #ffd6a5' }}>
                      <strong>🚚 Tracking:</strong> {detailOrder.trackingNumber}
                    </div>
                  )}
                  {detailOrder.shippedAt && (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
                      Shipped: {fmt(detailOrder.shippedAt)}
                      {detailOrder.deliveredAt && ` · Delivered: ${fmt(detailOrder.deliveredAt)}`}
                    </div>
                  )}
                </div>
              )}

              {/* QR Code preview */}
              {detailOrder.paymentStatus === 'SUCCESS' && (
                <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 14, border: '1px solid #dee2e6' }}>
                  <div style={{ fontWeight: 700, color: '#333', marginBottom: 10, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    📱 QR Code
                  </div>
                  {qrLoading ? (
                    <div style={{ textAlign: 'center', padding: 16, color: '#666' }}>
                      <div className="spinner" style={{ margin: '0 auto 8px' }}/>
                      Loading QR code...
                    </div>
                  ) : qrDataUrl ? (
                    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                      <img
                        src={qrDataUrl}
                        alt="QR Code"
                        style={{ width: 130, height: 130, border: '2px solid #ddd', borderRadius: 8, padding: 4, background: '#fff' }}
                      />
                      <div style={{ fontSize: 13, color: '#555', lineHeight: 2 }}>
                        <div><strong>Entity:</strong> {detailOrder.entityType}</div>
                        <div><strong>Order Type:</strong> {detailOrder.orderType}</div>
                        {detailOrder.orderType === 'PHYSICAL' && (
                          <>
                            <div><strong>Material:</strong> {detailOrder.materialType || 'PAPER'}</div>
                            <div style={{
                              marginTop: 8, padding: '6px 10px', borderRadius: 4, fontSize: 12,
                              background: detailOrder.materialType === 'VINYL' ? '#fff3cd' : '#d4edda',
                              color: detailOrder.materialType === 'VINYL' ? '#856404' : '#155724'
                            }}>
                              {detailOrder.materialType === 'VINYL'
                                ? '⚠️ Use vinyl label printer (Zebra / Brother QL)'
                                : '🖨️ Standard inkjet or laser printer'}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: '#888', fontSize: 12 }}>
                      QR preview not available — it will appear in the PDF if the backend supports the download endpoint.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDetailOrder(null)}>Close</button>
              {/* Two separate print buttons */}
              {detailOrder.paymentStatus === 'SUCCESS' && (
                <button
                  className="btn btn-primary"
                  onClick={() => printQrCode(detailOrder, qrDataUrl)}
                  style={{ background: '#0066cc', borderColor: '#0066cc', marginRight: 8 }}
                  title="Print QR sticker on photo paper or vinyl — credit card size"
                >
                  📱 Print QR Code
                </button>
              )}
              {detailOrder.orderType === 'PHYSICAL' && (
                <button
                  className="btn btn-primary"
                  onClick={() => printAddressLabel(detailOrder)}
                  style={{ background: '#28a745', borderColor: '#28a745' }}
                  title="Print shipping address label on A4 paper"
                >
                  📬 Print Address Label
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Manage Status Modal ─────────────────────────────────── */}
      {selected && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div className="modal">
            <div className="modal-header">
              <h3>⚙️ Manage Order</h3>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="modal-body">
              {message && message.type === 'success' && (
                <div className={`alert-banner ${message.type}`}>{message.text}</div>
              )}
              <div className="detail-grid" style={{ marginBottom: 16 }}>
                {[
                  ['Order Number', <span style={{ fontSize: 11 }}>{selected.orderNumber}</span>],
                  ['Amount',       `₹${selected.totalAmount}`],
                  ['Customer',     selected.userName],
                  ['Phone',        selected.userPhone],
                  ['Order Type',   selected.orderType],
                  ['Entity',       selected.entityType],
                  ['Payment',      <span className={`badge ${paymentBadge(selected.paymentStatus)}`}>{selected.paymentStatus}</span>],
                  ['Payment ID',   <span style={{ fontSize: 11 }}>{selected.paymentId || '—'}</span>],
                ].map(([label, val]) => (
                  <div key={label} className="detail-item">
                    <label>{label}</label><span>{val}</span>
                  </div>
                ))}
              </div>

              {selected.orderType === 'PHYSICAL' && (
                <div style={{ background: '#f8f9fa', borderRadius: 6, padding: 12, marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>📍 Shipping Address</div>
                  <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                    <strong>{selected.shippingName}</strong> | {selected.shippingPhone}<br/>
                    {selected.shippingAddressLine1}
                    {selected.shippingAddressLine2 && <>, {selected.shippingAddressLine2}</>}<br/>
                    {selected.shippingCity}, {selected.shippingState} — {selected.shippingPincode}
                    {selected.shippingLandmark && <><br/>Landmark: {selected.shippingLandmark}</>}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Update Order Status</label>
                <select className="form-control" value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                  {getAllowedStatuses(selected.orderStatus, selected.orderType).length === 0 ? (
                    <option value={selected.orderStatus}>{selected.orderStatus} (no further changes)</option>
                  ) : (
                    <>
                      <option value={selected.orderStatus}>{selected.orderStatus} (current)</option>
                      {getAllowedStatuses(selected.orderStatus, selected.orderType).map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </>
                  )}
                </select>
                {selected.orderType === 'PHYSICAL' && selected.orderStatus === 'PROCESSING' && (
                  <div style={{ fontSize: 11, color: '#856404', marginTop: 4, padding: '4px 8px', background: '#fff3cd', borderRadius: 4 }}>
                    ⚠️ Must mark as SHIPPED with tracking number before DELIVERED.
                  </div>
                )}
              </div>

              {(newStatus === 'SHIPPED' || selected.trackingNumber) && (
                <div className="form-group">
                  <label>Tracking Number</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter courier tracking number"
                    value={trackingNumber}
                    onChange={e => { setTrackingNumber(e.target.value); setMessage(null); }}
                  />
                  {message && message.type === 'error' && (
                    <div style={{
                      color: '#dc3545', fontSize: 12, marginTop: 6,
                      padding: '6px 10px', background: '#fff5f5',
                      borderRadius: 4, border: '1px solid #f5c6cb'
                    }}>
                      ⚠️ {message.text}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelected(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleUpdate} disabled={updating}>
                {updating ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
